import { useState, useEffect } from 'react';
import { listFeatureRuns, promoteForecastRun } from '../../api/analytics';

const BINARY_METRICS = ['auc', 'accuracy', 'precision', 'recall', 'f1'];
const REGRESSION_METRICS = ['rmse', 'mae', 'r2'];

function getMetricKeys(forecastType, runs) {
  if (forecastType === 'time_to_close') return REGRESSION_METRICS;
  if (forecastType === 'multi_class') {
    // Collect all f1_* keys from runs
    const keys = new Set();
    for (const run of runs) {
      if (run.metrics) {
        for (const k of Object.keys(run.metrics)) {
          if (k.startsWith('f1_') || k === 'accuracy') keys.add(k);
        }
      }
    }
    return ['accuracy', ...Array.from(keys).filter(k => k !== 'accuracy').sort()];
  }
  return BINARY_METRICS;
}

export default function ModelVersionComparison({ configId, forecastType }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(null);
  const [error, setError] = useState(null);

  function load() {
    setLoading(true);
    listFeatureRuns(configId)
      .then(data => { const d = data?.data ?? data; setRuns(Array.isArray(d) ? d : (d?.runs || [])); setLoading(false); })
      .catch(err => { setError(err.message || 'Failed to load runs'); setLoading(false); });
  }

  useEffect(() => { load(); }, [configId]);

  async function handlePromote(runId) {
    setPromoting(runId);
    try {
      await promoteForecastRun(configId, runId);
      load();
    } catch (err) {
      setError(err.message || 'Promote failed');
    } finally {
      setPromoting(null);
    }
  }

  if (loading) return <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading runs…</div>;
  if (error) return <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</div>;

  const trainedRuns = runs.filter(r => r.status === 'trained');
  if (!trainedRuns.length) return <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>No trained runs yet.</div>;

  const metricKeys = getMetricKeys(forecastType, trainedRuns);

  return (
    <div style={{ overflowX: 'auto', fontSize: '0.875rem' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '500px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
            <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: '#374151' }}>Version</th>
            <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: '#374151' }}>Status</th>
            {metricKeys.map(k => (
              <th key={k} style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600, color: '#374151' }}>{k}</th>
            ))}
            <th style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600, color: '#374151' }}>Active</th>
            <th style={{ padding: '6px 10px' }} />
          </tr>
        </thead>
        <tbody>
          {trainedRuns.map(run => (
            <tr key={run.id} style={{ borderBottom: '1px solid #f3f4f6', background: run.is_active ? '#eff6ff' : 'transparent' }}>
              <td style={{ padding: '6px 10px', color: '#111827' }}>v{run.version}</td>
              <td style={{ padding: '6px 10px', color: '#059669' }}>trained</td>
              {metricKeys.map(k => (
                <td key={k} style={{ padding: '6px 10px', textAlign: 'right', color: '#374151' }}>
                  {run.metrics?.[k] != null ? run.metrics[k].toFixed(4) : '—'}
                </td>
              ))}
              <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                {run.is_active ? '✓' : ''}
              </td>
              <td style={{ padding: '6px 10px' }}>
                {!run.is_active && (
                  <button onClick={() => handlePromote(run.id)} disabled={promoting === run.id}
                    style={{ padding: '3px 10px', borderRadius: '4px', border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer', fontSize: '0.8125rem' }}>
                    {promoting === run.id ? 'Promoting…' : 'Promote'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
